import React, { useState, useEffect } from 'react';

const CountdownDisplay = ({ complaint }) => {
    const [display, setDisplay] = useState(null);
    const [isDelayed, setIsDelayed] = useState(false);
    const [isPaused, setIsPaused] = useState(false);

    useEffect(() => {
        // Only show countdown if status IS 'in-operation'
        if (!complaint || complaint.status !== 'in-operation') {
            setDisplay(null);
            setIsDelayed(false);
            setIsPaused(false);
            return;
        }

        const calculateTime = () => {
            const hoursMatch = complaint.approximateResolveTime?.match(/(\d+)/);
            if (!hoursMatch) {
                setDisplay(null);
                return;
            }

            const totalWorkingHours = parseInt(hoursMatch[1]);
            const updatedAt = complaint.updatedAt?.toDate?.() || new Date(complaint.updatedAt);

            if (!updatedAt) {
                setDisplay(null);
                return;
            }

            const now = new Date();

            // Calculate working minutes between two dates efficiently
            const getWorkingMinutes = (startDate, endDate) => {
                // If start and end are the same day
                if (startDate.toDateString() === endDate.toDateString()) {
                    const startHour = startDate.getHours();
                    const startMinute = startDate.getMinutes();
                    const endHour = endDate.getHours();
                    const endMinute = endDate.getMinutes();

                    // Check if time range overlaps with working hours (9AM - 10PM)
                    const workStart = Math.max(startHour, 9);
                    const workEnd = Math.min(endHour, 22);

                    if (workStart < workEnd) {
                        let minutes = (workEnd - workStart) * 60;
                        // Adjust for minutes
                        if (startHour < 9) {
                            // Started before 9AM, start counting from 9AM
                            minutes -= startMinute;
                        } else {
                            minutes -= startMinute;
                        }
                        if (endHour > 22) {
                            // Ended after 10PM, stop at 10PM
                            minutes += (60 - endMinute);
                        } else {
                            minutes += (60 - endMinute);
                        }
                        return Math.max(0, minutes);
                    }
                    return 0;
                }

                // Different days - calculate full working hours for complete days
                let totalMinutes = 0;
                const start = new Date(startDate);
                const end = new Date(endDate);

                // If start date is the same as today, we need to handle it differently
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const startDay = new Date(startDate);
                startDay.setHours(0, 0, 0, 0);
                const endDay = new Date(endDate);
                endDay.setHours(0, 0, 0, 0);

                // Calculate full working days between start and end
                let currentDay = new Date(startDay);
                while (currentDay <= endDay) {
                    const dayStart = new Date(currentDay);
                    dayStart.setHours(9, 0, 0, 0);
                    const dayEnd = new Date(currentDay);
                    dayEnd.setHours(22, 0, 0, 0);

                    if (currentDay.getTime() === startDay.getTime()) {
                        // First day - only count from start time to 10PM
                        const actualStart = new Date(startDate);
                        const effectiveStart = actualStart > dayStart ? actualStart : dayStart;
                        if (effectiveStart < dayEnd) {
                            const minutes = (dayEnd - effectiveStart) / (1000 * 60);
                            totalMinutes += Math.max(0, minutes);
                        }
                    } else if (currentDay.getTime() === endDay.getTime()) {
                        // Last day - only count from 9AM to end time
                        const actualEnd = new Date(endDate);
                        const effectiveEnd = actualEnd < dayEnd ? actualEnd : dayEnd;
                        if (dayStart < effectiveEnd) {
                            const minutes = (effectiveEnd - dayStart) / (1000 * 60);
                            totalMinutes += Math.max(0, minutes);
                        }
                    } else {
                        // Full working day - 13 hours (9AM to 10PM)
                        totalMinutes += 13 * 60;
                    }

                    currentDay.setDate(currentDay.getDate() + 1);
                }

                return totalMinutes;
            };

            // Calculate working minutes between updatedAt and now
            const elapsedWorkingMinutes = getWorkingMinutes(updatedAt, now);
            const totalWorkingMinutesAvailable = totalWorkingHours * 60;
            const remainingWorkingMinutes = totalWorkingMinutesAvailable - elapsedWorkingMinutes;

            // Check if currently in working hours
            const currentHour = now.getHours();
            const currentMinute = now.getMinutes();
            const isWorkingHour = currentHour >= 9 && currentHour < 22;

            // If not in working hours and still has time remaining, show paused state
            if (!isWorkingHour && remainingWorkingMinutes > 0) {
                const nextWorkStart = new Date(now);
                nextWorkStart.setHours(9, 0, 0, 0);
                nextWorkStart.setDate(nextWorkStart.getDate() + 1);

                const pauseRemaining = nextWorkStart - now;
                const hours = Math.floor(pauseRemaining / (1000 * 60 * 60));
                const minutes = Math.floor((pauseRemaining % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((pauseRemaining % (1000 * 60)) / 1000);

                setDisplay({ hours, minutes, seconds });
                setIsDelayed(false);
                setIsPaused(true);
                return;
            }

            setIsPaused(false);

            if (remainingWorkingMinutes > 0) {
                // Still have time remaining
                const remainingMs = remainingWorkingMinutes * 60 * 1000;
                const hours = Math.floor(remainingMs / (1000 * 60 * 60));
                const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);

                setDisplay({ hours, minutes, seconds });
                setIsDelayed(false);
            } else {
                // Delayed - over time
                const delayMinutes = Math.abs(remainingWorkingMinutes);
                const hours = Math.floor(delayMinutes / 60);
                const minutes = Math.floor(delayMinutes % 60);

                setDisplay({ hours, minutes, seconds: 0 });
                setIsDelayed(true);
            }
        };

        calculateTime();
        const interval = setInterval(calculateTime, 1000);

        return () => clearInterval(interval);
    }, [complaint?.id, complaint?.status, complaint?.approximateResolveTime, complaint?.updatedAt]);

    // Don't show countdown if not 'in-operation'
    if (complaint?.status !== 'in-operation' || !display) {
        return <span className="text-gray-400 text-xs">-</span>;
    }

    const { hours, minutes, seconds } = display;

    // Show paused state (outside working hours)
    if (isPaused) {
        return (
            <div className="flex items-center gap-1 text-gray-500">
                <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded-full">⏸️</span>
                <span className="text-xs whitespace-nowrap">
                    Resumes in {hours > 0 && `${hours}h `}{minutes}m {seconds}s
                </span>
            </div>
        );
    }

    // Show delayed state
    if (isDelayed) {
        return (
            <div className="flex items-center gap-1 text-red-600 font-semibold">
                <span className="text-xs bg-red-100 px-1.5 py-0.5 rounded-full">⏰</span>
                <span className="text-base whitespace-nowrap">
                    +{hours}h {minutes}m
                </span>
            </div>
        );
    }

    // Normal countdown
    const isUrgent = hours < 1;

    return (
        <div className={`flex items-center gap-1 ${isUrgent ? 'text-orange-600' : 'text-blue-600'}`}>
            <span className={`text-base ${isUrgent ? 'animate-pulse' : ''}`}>
                {hours > 0 && `${hours}h `}
                {minutes}m {seconds}s
            </span>
        </div>
    );
};

export default CountdownDisplay;